import { withDashboardLayout } from 'layouts/DashboardLayout/DashboardLayout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAlert } from 'context/AlertProvider';
import PageContainer from 'components/PageContainer/PageContainer';
import {
  Button,
  Card,
  Confirm,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import RetryShipmentsTable from 'components/RetryShipmentsTable/RetryShipmentsTable';
import {
  getAll,
  getRetryableTotals,
  remove,
  update,
} from 'services/shipments.service';
import {
  createForRetries as createPreparationOrder,
  setAsWaiting as setPreparationOrderAsWaiting,
} from 'services/preparation-orders.service';
import { create as createWorkingOrder } from 'services/working-orders.service';
import Portal from 'components/Portal/Portal';
import { v4 } from 'uuid';
import {
  isB2B,
  isOllas,
  isSATUploader,
  isSorterRetry,
} from 'modules/user-has-permission/user-has-permission';
import { runSequentially } from 'modules/run-sequentially/run-sequentially';
import { ITEMS_PER_PAGE, ORIGINS, STATUSES } from 'constants/constants';
import TableWrapper from 'components/TableWrapper/TableWrapper';
import { useResetSelectedRows } from 'hooks/useResetSelectedRows';
import RetryShipmentsFiltersBar from 'components/RetryShipmentsFiltersBar/RetryShipmentsFiltersBar';
import useFiltersReducer, {
  loadMoreAction,
  updateFiltersAction,
} from 'hooks/useFiltersReducer';
import { useOrigin } from 'hooks/useOrigin';
import Spacer from 'components/Spacer/Spacer';
import { useLocalFilteredResults } from 'hooks/useLocalFilteredResults';
import { useGroupId } from 'hooks/useGroupId';
import './retry-shipments-page.scss';
import { useUser } from 'hooks/redux/useUser';
import {
  createForRetries as createSorterPreparationOrder,
  setAsWaiting as setSorterPreparationOrderAsWaiting,
} from 'services/sorter-preparation-orders.service';
import { useListToCsv } from 'hooks/useListToCsv';
import { retryShipmentsCSVColumns } from './columns';

function RetryShipmentsPage({ origin }) {
  const alert = useAlert();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteIsVisible, setConfirmDeleteIsVisible] = useState(false);
  const [confirmRetryIsVisible, setConfirmRetryIsVisible] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const {
    user: { email: userEmail },
  } = useUser();
  const [hasNextPage, setHasNextPage] = useState(true);
  const [query, setQuery] = useState('');
  const mustCreatePreparationOrder = useMemo(() => isB2B(), []);
  const mustCreateWorkingOrder = useMemo(
    () => isSATUploader() || isOllas(),
    [],
  );
  const mustCreateSorterPreparationOrder = useMemo(() => isSorterRetry(), []);
  const { lastResetSelectedRows, resetSelectedRows } = useResetSelectedRows();
  const userOrigin = useOrigin(origin);
  const group_id = useGroupId(origin);
  const [totals, setTotals] = useState([]);
  const [fetchingTotals, setFetchingTotals] = useState(false);

  const [state, dispatch] = useFiltersReducer({
    agency_id: '',
    warehouse_id: '',
    page: 1,
  });

  const { saveCSVFromList } = useListToCsv();

  const fetchShipments = useCallback(
    async (state) => {
      if (!group_id) return;
      try {
        setLoading(true);
        const { data: failedShipments } = await getAll({
          warehouse_id: state.warehouse_id,
          status: {
            in: `${STATUSES.FIXING.name},${STATUSES.INVALID.name}`,
          },
          group_id,
          agency_id: state.agency_id,
          page: state.page,
          itemsPerPage: ITEMS_PER_PAGE,
        });
        setHasNextPage(failedShipments?.length === ITEMS_PER_PAGE);
        setShipments((prev) =>
          state.page > 1 ? [...prev, ...failedShipments] : failedShipments,
        );
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    },
    [setHasNextPage, alert, group_id],
  );

  const fetchTotals = useCallback(async () => {
    try {
      setFetchingTotals(true);
      const { data: totals } = await getRetryableTotals(group_id);
      setTotals(totals);
    } catch (error) {
      alert.current.error(getMessageFromAxiosError(error));
    } finally {
      setFetchingTotals(false);
    }
  }, [alert, group_id]);

  const resetView = useCallback(async () => {
    await fetchShipments(state);
    await fetchTotals();
    resetSelectedRows();
  }, [state, fetchShipments, resetSelectedRows, fetchTotals]);

  const updateShipments = useCallback(
    async (shipmentReferences) => {
      const results = {
        success: [],
        error: [],
      };
      try {
        setUpdating(true);
        setConfirmRetryIsVisible(false);
        const uuid = v4();

        let createdPreparationOrderId = null;
        let createdWorkingOrderId = null;
        let createdSorterPreparationOrderId = null;

        if (
          mustCreatePreparationOrder &&
          userOrigin !== ORIGINS.SCANPACK.slug &&
          userOrigin !== ORIGINS.SORTER.slug &&
          userOrigin !== ORIGINS.BULKDROP.slug
        ) {
          const { data: createdPreparationOrder } =
            await createPreparationOrder({
              request_id: uuid,
              recipient: userEmail,
              agency_id: state.agency_id,
              warehouse_id: state.warehouse_id,
            });
          createdPreparationOrderId = createdPreparationOrder?.id;
        } else if (
          mustCreateWorkingOrder &&
          userOrigin !== ORIGINS.SCANPACK.slug &&
          userOrigin !== ORIGINS.SORTER.slug &&
          userOrigin !== ORIGINS.BULKDROP.slug
        ) {
          const { data: createdWorkingOrder } = await createWorkingOrder(
            userEmail,
            uuid,
          );
          createdWorkingOrderId = createdWorkingOrder?.id;
        } else if (
          mustCreateSorterPreparationOrder &&
          userOrigin !== ORIGINS.SCANPACK.slug &&
          userOrigin === ORIGINS.SORTER.slug &&
          userOrigin !== ORIGINS.BULKDROP.slug
        ) {
          const { data: createdSorterPreparationOrder } =
            await createSorterPreparationOrder({
              request_id: uuid,
              warehouse_id: state.warehouse_id,
            });
          createdSorterPreparationOrderId = createdSorterPreparationOrder?.id;
        }
        await runSequentially(
          shipmentReferences,
          async (shipment) => {
            try {
              await update(shipment.reference, {
                request_id:
                  userOrigin === ORIGINS.SCANPACK.slug &&
                  userOrigin === ORIGINS.BULKDROP.slug
                    ? v4()
                    : uuid,
                ...(mustCreateSorterPreparationOrder && {
                  sorter_preparation_order_id: createdSorterPreparationOrderId,
                }),
                ...(mustCreatePreparationOrder && {
                  preparation_order_id: createdPreparationOrderId,
                }),
                ...(mustCreateWorkingOrder && {
                  working_order_id: createdWorkingOrderId,
                }),
              });

              results.success = [...results.success, shipment.reference];
            } catch (error) {
              results.error = [...results.error, shipment.reference];
            }
          },
          {
            msBetweenEachRun: 500,
          },
        );

        try {
          if (
            !!mustCreateSorterPreparationOrder &&
            userOrigin !== ORIGINS.SCANPACK.slug &&
            userOrigin === ORIGINS.SORTER.slug &&
            userOrigin !== ORIGINS.BULKDROP.slug
          ) {
            await setSorterPreparationOrderAsWaiting(
              createdSorterPreparationOrderId,
            );
          } else if (
            mustCreatePreparationOrder &&
            userOrigin !== ORIGINS.SCANPACK.slug &&
            userOrigin !== ORIGINS.SORTER.slug &&
            userOrigin !== ORIGINS.BULKDROP.slug
          ) {
            await setPreparationOrderAsWaiting(createdPreparationOrderId);
          }
        } catch (error) {
          alert.current.error(getMessageFromAxiosError(error));
        }

        if (!!results.success.length && !results.error.length) {
          alert.current.message(
            'Proceso de reparación finalizado con éxito',
            async () => {
              await resetView();
            },
          );
        }

        if (!!results.success.length && !!results.error.length) {
          alert.current.message(
            `<p>El proceso de reparación ha finalizado con los siguientes resultados:</p>
            <p>
              <small><strong>Referencias incluídas:</strong><br />${results.success.join(
                ', ',
              )}</small>
            </p>
            <p>
              <small><strong>Referencias no incluídas:</strong><br />${results.error.join(
                ', ',
              )}</small>
            </p>`,
            async () => {
              await resetView();
            },
          );
        }
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setUpdating(false);
      }
    },
    [
      userEmail,
      alert,
      mustCreatePreparationOrder,
      mustCreateWorkingOrder,
      mustCreateSorterPreparationOrder,
      resetView,
      userOrigin,
      state,
    ],
  );

  const deleteShipments = useCallback(
    async (shipments) => {
      try {
        setDeleting(true);
        const shipmentReferences = shipments.map(
          (shipment) => shipment.reference,
        );
        await remove(shipmentReferences);
        alert.current.message(
          'Proceso de borrado finalizado con éxito',
          async () => {
            setConfirmDeleteIsVisible(false);
            await resetView();
          },
        );
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setDeleting(false);
      }
    },
    [alert, resetView],
  );

  const renderTotalShipments = useCallback((shipments) => {
    const INVALID = shipments.filter(
      (shipment) => shipment.status === STATUSES.INVALID.name,
    )?.length;
    const FIXING = shipments.filter(
      (shipment) => shipment.status === STATUSES.FIXING.name,
    )?.length;
    return (
      <small>
        <strong>Total {shipments.length}:</strong>{' '}
        {!!INVALID && `${INVALID} erróneos`}{' '}
        {!!FIXING && `${FIXING} pendientes de arreglo`}
      </small>
    );
  }, []);

  const filteredShipments = useLocalFilteredResults(shipments, query);

  const getFixingRetries = useCallback((selectedShipments) => {
    return (selectedShipments || []).filter(
      (selectedShipment) => selectedShipment.status === STATUSES.FIXING.name,
    );
  }, []);

  const renderConfirmContent = useCallback(() => {
    const fixinRetriesCount = getFixingRetries(selectedShipments).length;
    return fixinRetriesCount
      ? `De la selección, solo se reintentarán los envíos con el estado "Pendiente de arreglo": ${fixinRetriesCount} ${
          fixinRetriesCount === 1 ? 'envío' : 'envíos'
        }`
      : `La operación no puede continuar porque no hay envíos en estado "Pendiente de arreglo" seleccionados`;
  }, [selectedShipments, getFixingRetries]);

  const updateFilters = useCallback(
    (filters) => {
      dispatch(updateFiltersAction(filters));
    },
    [dispatch],
  );

  const selectedShipmentsToCSV = () => {
    saveCSVFromList(selectedShipments, retryShipmentsCSVColumns);
  };

  useEffect(() => {
    (async () => {
      if (!!group_id) await fetchTotals();
    })();
  }, [fetchTotals, group_id]);

  useEffect(() => {
    (async () => {
      if (
        (!!state?.warehouse_id && !!state?.agency_id) ||
        (!!state?.warehouse_id && origin === ORIGINS.SORTER.slug)
      )
        await fetchShipments(state);
    })();
  }, [state, fetchShipments, origin]);
  return (
    <PageContainer
      title={`Envíos fallidos ${ORIGINS[origin?.toUpperCase()]?.name || ''}`}
      className="retry-shipments-page"
    >
      <Dimmer active={loading && state.page === 1} inverted>
        <Loader>Cargando envíos…</Loader>
      </Dimmer>
      <Dimmer active={updating} inverted>
        <Loader>Actualizando envíos…</Loader>
      </Dimmer>
      <Dimmer active={deleting} inverted>
        <Loader>Borrando envíos…</Loader>
      </Dimmer>
      <Dimmer active={fetchingTotals} inverted>
        <Loader>Recuperando totales…</Loader>
      </Dimmer>
      <>
        <Card.Header>
          <Portal.Out name="card-header-left" />
          <Portal.Out name="card-header" />
        </Card.Header>
        <Card.Content>
          <RetryShipmentsFiltersBar
            onFiltersChange={updateFilters}
            query={query}
            totals={totals}
            userOrigin={userOrigin}
            onChangeQuery={(query) => {
              setQuery(query);
              resetSelectedRows();
            }}
          />
          <Spacer />
          {!!filteredShipments.length ? (
            <TableWrapper>
              <RetryShipmentsTable
                loading={loading}
                origin={userOrigin}
                shipments={filteredShipments}
                hasNextPage={hasNextPage}
                lastResetSelectedRows={lastResetSelectedRows}
                onRowSelect={(selectedShipments) =>
                  setSelectedShipments(selectedShipments)
                }
                onLoadMoreItems={async (skip) => {
                  const isFirstLoad = skip === 0;
                  if (isFirstLoad) return;
                  dispatch(loadMoreAction());
                }}
              />
              <footer className="retry-shipments-page__table-footer">
                <small>
                  {!!selectedShipments?.length && selectedShipments.length === 1
                    ? '1 seleccionado'
                    : `${selectedShipments.length} seleccionados`}
                </small>
                {!!filteredShipments?.length &&
                  renderTotalShipments(filteredShipments)}
              </footer>
            </TableWrapper>
          ) : (
            <Message info>
              {!!state.agency_id && !!state.warehouse_id
                ? `No se encuentra ningún envío fallido…`
                : 'Selecciona un almacén y una agencia para recuperar envíos fallidos'}
            </Message>
          )}
        </Card.Content>
        <Portal.In target="card-header">
          <>
            <>
              <Button
                icon="download"
                content="Descargar CSV"
                disabled={Boolean(!selectedShipments?.length)}
                onClick={selectedShipmentsToCSV}
              />
              <Button
                icon="trash alternate outline"
                content="Borrar"
                disabled={Boolean(!selectedShipments?.length)}
                onClick={() => setConfirmDeleteIsVisible(true)}
              />
              <Confirm
                open={confirmDeleteIsVisible}
                content={`¿Confirmas el borrado de los envíos seleccionadas? Esta operación es irreversible`}
                cancelButton="Cancelar"
                confirmButton={`Borrar ${selectedShipments.length || 0} ${
                  selectedShipments.length === 1 ? 'envío' : 'envíos'
                }`}
                onCancel={(event, data) => setConfirmDeleteIsVisible(false)}
                onConfirm={() => deleteShipments(selectedShipments)}
              />
            </>
            <>
              <Button
                icon="wrench"
                content="Reparar"
                disabled={Boolean(!selectedShipments?.length)}
                primary
                onClick={() => setConfirmRetryIsVisible(true)}
              />
              <Confirm
                open={confirmRetryIsVisible}
                content={renderConfirmContent()}
                cancelButton="Cancelar"
                confirmButton={
                  !getFixingRetries(selectedShipments).length
                    ? false
                    : `Reparar ${
                        getFixingRetries(selectedShipments).length || 0
                      } ${
                        getFixingRetries(selectedShipments).length === 1
                          ? 'envío'
                          : 'envíos'
                      }`
                }
                onCancel={(event, data) => setConfirmRetryIsVisible(false)}
                onConfirm={() =>
                  updateShipments(getFixingRetries(selectedShipments))
                }
              />
            </>
          </>
        </Portal.In>
      </>
    </PageContainer>
  );
}

export default withDashboardLayout(RetryShipmentsPage);
